// ============================================================
// ELC Shift — 📧 Automated emails hub (v131.26)
//
// Matt, 8 Sep (after hunting for the briefing buttons): "we should just have
// a tab that does emails that we are sending out, with the ability to type
// in / amend the audience." One card per automated email: what it is, when
// it sends, on/off, the audience (editable where it IS a list), test + send
// buttons, and the last attempt. Config goes through the hub endpoint
// (/api/admin/email-hub); SENDING stays on each email's own endpoint — the
// hub is a front door, not a re-implementation.
//
// Free globals consumed: DB, escapeHtml, showToast (admin.html inline core).
// ============================================================

// v133.56 (Matt, 6 Oct 2026: too many Monday reports): the day and frequency of the movable weekly
// emails. The rules and labels are shared with the Worker's cron (src/shared/email-schedule.js).
import { FREQS, DAY_NAMES, describeSchedule, coversNote, isDueOn } from '../shared/email-schedule.js';

// The catalogue mirrors src/worker/routes/email-hub.js EMAIL_HUB_CATALOG ids.
// audience 'list' renders the editable textarea; 'role'/'env' render a note.
var HUB_CATALOG = [
  // v132.62 — the daily business brief. First in the list because it is the
  // one that goes out every day. v133.71: a typed list (Matt's choice, 6 Oct
  // 2026); left empty it is every Head Office account and area manager.
  { id: 'daily_brief', name: '📈 Daily business brief',
    when: 'Every morning',
    audienceKind: 'Who gets it',   // v133.71 (Matt, 6 Oct 2026: "Fully free-form list"): the brief's own typed list
    listNote: 'Leave it empty and it goes to everyone at Head Office and every area manager with an email on their account.',
    test: { path: '/api/admin/daily-brief-email/preview', method: 'POST', body: function () { return {}; }, label: '📧 Preview to me' },
    send: { path: '/api/admin/daily-brief-email/send-now', method: 'POST', body: function () { return {}; } } },
  // v132.64 - Google reviews, measured. Nightly snapshot of each listing's
  // public figures; this Monday email is the arithmetic between them.
  { id: 'google_reviews', name: '⭐ Google reviews (weekly)',
    when: 'Every Monday morning',
    audienceNote: 'Everyone at Head Office with an email on their account. Numbers come from a nightly snapshot of each store Google listing; the Worker needs GOOGLE_PLACES_KEY for that to run.',
    test: { path: '/api/admin/google-reviews/preview', method: 'POST', body: function () { return {}; }, label: '📧 Preview to me' },
    send: { path: '/api/admin/google-reviews/send-now', method: 'POST', body: function () { return {}; } } },
  // v136.83 (Matt, 1 Nov 2026): a Google review under 4 stars, the night it appears. Email to the list below,
  // and a phone notification to anyone on it with a Shift account and notifications on. Nothing to preview: it
  // only sends when there is a new low review.
  { id: 'google_low_review', name: '⚠ Google review under 4 stars (alert)',
    when: 'Every night after the Google check, only when a new review under 4 stars appears',
    audienceKind: 'Who gets it',
    listNote: 'Each address gets every alert. The area manager of the store also gets the alerts for their own stores, without being on this list. Anyone with a Shift account and notifications switched on also gets a phone notification. Google shows Shift five reviews per store, so a low review can be missed.' },
  // v132.75: the store EOD email. Each store sends its own from the EOD screen,
  // after typing today's sales and demos and confirming with a PIN. This card
  // is the switch (OFF until Head Office turns it on) and the inboxes it goes to.
  { id: 'eod', name: '🧾 Store EOD emails (sent by each store)',
    when: 'Each evening, when a store confirms its figures on the EOD screen. Never sent automatically.',
    audienceKind: 'The EOD inboxes every store email goes to' },
  // v132.79: last week's customer requests (items we don't stock) for buying:
  // an AI summary of themes, then the raw data and a CSV. Preview goes to you only.
  { id: 'customer_requests', name: '🛍 Customer requests (weekly, to buying)',
    when: 'Every Monday morning, for the week that ended on Sunday',
    audienceKind: 'The buying team',
    test: { path: '/api/admin/customer-requests/preview', method: 'POST', body: function () { return {}; }, label: '📧 Preview to me' },
    send: { path: '/api/admin/customer-requests/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'am_briefing', name: '🧭 Who to talk to (compliance briefing)',
    when: 'Every Monday morning', audienceNote: 'Each area manager gets their own section (by role). Extra people: tick "Send them the compliance briefing" on their account.',
    test: { path: '/api/admin/am-briefing/preview', method: 'POST', body: function () { return {}; }, label: '📧 Preview to me' },
    send: { path: '/api/admin/am-briefing/send-now', method: 'POST', body: function () { return {}; } } },
  // v132.1 — the list IS the approver group. "latest" = next week's draft; the
  // Worker answers 404 with a plain sentence when there is no draft yet.
  { id: 'training_approval', name: '🎓 Weekly training — approval request',
    when: 'Every Monday 05:00 Dubai, for the FOLLOWING week (PR 4); or from the Training tab',
    audienceNote: 'Each address must be a Shift account — they get personal, single-use approve/decline links. The switch also gates the Monday publish.',
    test: { path: '/api/admin/training/latest/preview', method: 'POST', body: function () { return {}; }, label: '📧 Preview to me' },
    send: { path: '/api/admin/training/latest/send-approval', method: 'POST', body: function () { return {}; } } },
  { id: 'weekly_email', name: '📊 Weekly sales email (UK team)',
    when: 'Every Wednesday', audienceNote: 'Set in the server settings (WEEKLY_EMAIL_TO) — ask Matt to change it.',
    test: { path: '/api/admin/weekly-email/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/weekly-email/send-now', method: 'POST', body: function () { return {}; } } },
  // v132.66: this chases the QUARTERLY STAFF REVIEWS (who has not been reviewed
  // yet), not Google reviews; the old label sent people to the wrong place.
  { id: 'review_chase', name: '⭐ Review chase (quarterly staff reviews)',
    when: 'Mondays in the chase window, quarterly', audienceKind: 'CC list — the area managers are always on it',
    test: { path: '/api/admin/review-chase', method: 'POST', body: function (to) { return { send: true, testTo: to }; }, ask: true },
    send: { path: '/api/admin/review-chase', method: 'POST', body: function () { return { send: true }; } } },
  { id: 'chat_digest', name: '💬 Chat digest (AI summary of store chat)',
    when: 'Weekly',
    test: { path: '/api/admin/chat-digest/run', method: 'POST', body: function (to) { return { test: true, to: to }; }, ask: true },
    send: { path: '/api/admin/chat-digest/run', method: 'POST', body: function () { return {}; } } },
  { id: 'svr_monthly', name: '📋 Monthly store-visit report',
    when: '1st–3rd of each month, about the month just ended',
    test: { path: '/api/admin/svr-monthly/send', method: 'POST', body: function (to) { return { test_to: to }; }, ask: true },
    send: { path: '/api/admin/svr-monthly/send', method: 'POST', body: function () { return {}; } } },
  // The customer service console's weekly AI review (WhatsApp + website
  // customers): what they wanted, what needs attention, and the questions the
  // assistant could answer next time, with draft FAQs.
  { id: 'cs_weekly', name: '💬 Customer service weekly review',
    when: 'Every Monday about 10:30am, for the week that ended on Sunday',
    audienceKind: 'Whoever runs customer service',
    test: { path: '/api/admin/cs-weekly/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/cs-weekly/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'jack_digest', name: '🤖 Jack search digest (what stores are asking)',
    when: 'Every Monday morning',
    test: { path: '/api/admin/jack-digest/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/jack-digest/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'reviews_outstanding', name: '📋 Quarterly reviews outstanding',
    when: '1st–3rd of each month — who still needs a final review this quarter, by store',
    test: { path: '/api/admin/reviews-outstanding/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/reviews-outstanding/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'induction_progress', name: '🎓 Induction module progress',
    when: 'Every Monday — modules signed off per person, by store; not-started flagged',
    test: { path: '/api/admin/induction-progress/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/induction-progress/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'unanswered_dm', name: '💬 Unanswered store messages',
    when: 'Daily — each Head Office person gets their own list of store messages with no reply after 4 hours; stops by itself once they reply',
    audienceNote: 'Derived automatically: whoever holds an unanswered store message gets the email, to their account email address. No list to maintain.',
    test: { path: '/api/admin/unanswered-dm/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/unanswered-dm/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'security_digest', name: '🛡️ Security digest',
    when: 'Daily — failed logins, PIN lockouts, wrong store passwords, Head Office logins from a new country, account / password / config changes, bulk deletes, live sessions; a quiet day says so',
    test: { path: '/api/admin/security-digest/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/security-digest/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'negative_balances', name: '⚠️ Negative TOIL balances',
    when: 'Daily at 7am — only on days somebody is below zero TOIL, so an empty inbox means a clean book',
    audienceKind: 'Who reconciles these against Amanah (the ops manager). Leave it empty and it falls back to the server alert address.',
    test: { path: '/api/admin/negative-balances/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/negative-balances/send-now', method: 'POST', body: function () { return {}; } } },
  { id: 'holiday_status', name: '🏖 ELC holiday status',
    when: 'Every Monday — balances, booked leave, and the carry-risk list (same maths as the Holiday tracker)',
    test: { path: '/api/admin/holiday-status/test', method: 'POST', body: function (to) { return { to: to }; }, ask: true },
    send: { path: '/api/admin/holiday-status/send-now', method: 'POST', body: function () { return {}; } } },
  // 19 Oct 2026 (Matt): each approved leave request, and each cancellation of approved leave, to HR so HR can key it
  // into Amanah (src/worker/leave-hr.js). No switch (alwaysOn) and no test or send button: it goes when leave is
  // decided. The approver always gets a copy; the employee never does.
  { id: 'leave_hr', name: '🗓 Approved leave to HR (for Amanah)',
    when: 'Each time a leave request is approved, or approved leave is cancelled. Not on a schedule.',
    audienceKind: 'The HR inboxes',
    listNote: 'The person who approved it always gets a copy. Leave the list empty and only they get it, and the leave request says HR was not emailed.',
    alwaysOn: true },
];

// v133.71 (Matt, 6 Oct 2026: "could we have a summary on the top of what e-mails go out on each day (so i
// can understand how many go out per day)"): when each email goes, for the "next 7 days" box. The movable
// weekly emails follow their own day setting (d.schedule, the cron's own isDueOn), so they are not listed
// here; an email missing from here and from the schedules is simply not counted on any day.
export var CADENCE = {
  daily_brief: { daily: true },
  security_digest: { daily: true },
  unanswered_dm: { daily: true, only: 'only to someone with a store message unanswered after 4 hours' },
  negative_balances: { daily: true, only: 'only when someone is below zero' },
  google_low_review: { daily: true, only: 'only when a new Google review under 4 stars appears' },   // v136.83
  training_approval: { dow: 1 },
  cs_weekly: { dow: 1 },
  svr_monthly: { monthDay: 1, when: 'between the 1st and the 3rd' },
  reviews_outstanding: { monthDay: 1, when: 'between the 1st and the 3rd' },
  eod: { other: 'sent by each store when it confirms its figures, not on a schedule' },
  leave_hr: { other: 'sent when leave is approved or cancelled, not on a schedule' },
  review_chase: { other: 'Mondays in the run-up to quarter end only' },
};

function addDays(ymd, n) {
  var d = new Date(String(ymd).slice(0, 10) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Pure: the next 7 days from today (a Dubai date), counting only emails that are switched ON.
// { daily: [{ name, only }], days: [{ ymd, items: [{ name, when }], total }], other: [{ name, other }], off: [names] }
export function weekPlan(catalog, emails, todayYmd) {
  var on = [], off = [], daily = [], other = [];
  catalog.forEach(function (c) {
    var d = emails && emails[c.id];
    if (!d || !d.enabled) { off.push(c.name); return; }
    on.push(c);
    var k = CADENCE[c.id] || {};
    if (k.daily) daily.push({ name: c.name, only: k.only || '' });
    else if (k.other) other.push({ name: c.name, other: k.other });
  });
  var days = [];
  for (var i = 0; i < 7; i++) {
    var ymd = addDays(todayYmd, i), dt = new Date(ymd + 'T00:00:00Z');
    var items = [];
    on.forEach(function (c) {
      var k = CADENCE[c.id] || {}, sch = emails[c.id].schedule;
      if (k.daily || k.other) return;
      if (sch) { if (isDueOn(sch, ymd)) items.push({ name: c.name, when: sch.freq === 'weekly' ? '' : sch.freq }); return; }
      if (k.dow != null && dt.getUTCDay() === k.dow) items.push({ name: c.name, when: '' });
      if (k.monthDay && dt.getUTCDate() === k.monthDay) items.push({ name: c.name, when: k.when || '' });
    });
    days.push({ ymd: ymd, items: items, total: daily.length + items.length });
  }
  return { daily: daily, days: days, other: other, off: off };
}

function dubaiToday() {
  try { return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }); } catch (e) { return new Date(Date.now() + 4 * 3600000).toISOString().slice(0, 10); }   // Dubai is UTC+4, no daylight saving
}

function summaryHtml() {
  var esc = escapeHtml;
  var p = weekPlan(HUB_CATALOG, (_hubData && _hubData.emails) || {}, dubaiToday());
  var dayLabel = function (ymd) { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); };
  // v135.2: each word and each email name in its own span, so the Arabic layer can swap them one by one
  var dayHtml = function (ymd) { var b = dayLabel(ymd).split(' '); return b.length === 3 ? '<span>' + esc(b[0]) + '</span> ' + esc(b[1]) + ' <span>' + esc(b[2]) + '</span>' : esc(dayLabel(ymd)); };
  var rows = p.days.map(function (d, i) {
    var extra = d.items.length ? d.items.map(function (x) { return '<span>' + esc(x.name) + '</span>' + (x.when ? ' <span style="color:var(--sub)">(' + esc(x.when) + ')</span>' : ''); }).join(', ') : '<span style="color:var(--sub)">only the daily ones</span>';
    return '<tr' + (i === 0 ? ' style="font-weight:600"' : '') + '><td style="white-space:nowrap;padding:5px 10px 5px 0">' + dayHtml(d.ymd) + (i === 0 ? ' <span>(today)</span>' : '') + '</td>' +
      '<td style="white-space:nowrap;padding:5px 10px 5px 0;text-align:end;font-variant-numeric:tabular-nums"><b>' + d.total + '</b></td>' +
      '<td style="padding:5px 0;font-weight:400">' + extra + '</td></tr>';
  }).join('');
  var daily = p.daily.length
    ? '<div style="font-size:12.5px;margin-bottom:8px"><b>Every day (' + p.daily.length + '):</b> ' + p.daily.map(function (x) { return '<span>' + esc(x.name) + '</span>' + (x.only ? ' <span style="color:var(--sub)">(' + esc(x.only) + ')</span>' : ''); }).join(', ') + '</div>'
    : '';
  var notes = p.other.map(function (x) { return '<span>' + esc(x.name) + '</span>: <span>' + esc(x.other) + '</span>'; });
  if (p.off.length) notes.push('Switched off, so not counted: ' + p.off.map(function (n) { return '<span>' + esc(n) + '</span>'; }).join(', '));
  return '<div class="card" style="margin-bottom:14px">' +
    '<div style="font-weight:700;font-size:14px;margin-bottom:6px">📅 The next 7 days</div>' + daily +
    '<div style="overflow-x:auto"><table style="border-collapse:collapse;font-size:12.5px;width:100%"><thead><tr style="color:var(--sub);text-align:start"><th style="font-weight:600;padding:0 10px 4px 0">Day</th><th style="font-weight:600;padding:0 10px 4px 0;text-align:end">Emails</th><th style="font-weight:600;padding:0 0 4px">As well as the daily ones</th></tr></thead><tbody>' + rows + '</tbody></table></div>' +
    '<div style="font-size:11.5px;color:var(--sub);margin-top:8px">Counted by email, not by inbox: one email can go to several people. Emails marked "only" may not go on a quiet day.' + (notes.length ? '<br>' + notes.join('<br>') : '') + '</div>' +
  '</div>';
}

// v133.72 (Matt, 6 Oct 2026): a typed list does not follow the Accounts page, so an address misspelt when it
// was typed went on being used for weeks. Flag any address that is no Shift account, and suggest the account
// it was probably meant to be. A warning, never a block: a team inbox or someone without an account is fine.
function editDistance(a, b) {
  var m = a.length, n = b.length, prev = [], cur = [], i, j;
  for (j = 0; j <= n; j++) prev[j] = j;
  for (i = 1; i <= m; i++) {
    cur = [i];
    for (j = 1; j <= n; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[n];
}
// Pure: the addresses in a list that are no account, each with the closest account if it is only a letter
// or two away. [{ address, suggest }]
export function listWarnings(recipients, accountEmails) {
  var known = {};
  (accountEmails || []).forEach(function (e) { known[String(e).toLowerCase()] = true; });
  if (!Object.keys(known).length) return [];   // no accounts read: say nothing rather than flag everyone
  var out = [];
  (recipients || []).forEach(function (r) {
    var a = String(r || '').trim().toLowerCase();
    if (!a || known[a]) return;
    var best = null, bestD = 3;
    Object.keys(known).forEach(function (k) { var d = editDistance(a, k); if (d < bestD) { bestD = d; best = k; } });
    out.push({ address: String(r).trim(), suggest: best });
  });
  return out;
}
function warningsHtml(list) {
  if (!list.length) return '';
  var esc = escapeHtml;
  return '<div style="margin-top:6px;padding:8px 10px;border-radius:8px;background:var(--warn-bg);border:1px solid var(--ap-amber-line);font-size:12px;color:var(--warn-deep)">' +
    '<b>Not a Shift account, so check the spelling:</b>' +
    list.map(function (w) { return '<div><span translate="no">' + esc(w.address) + '</span>' + (w.suggest ? ': did you mean <b translate="no">' + esc(w.suggest) + '</b>?' : '') + '</div>'; }).join('') +
    '<div style="margin-top:4px;color:var(--sub)">A team inbox, or someone without a Shift account, is fine to keep.</div></div>';
}

// 19 Oct 2026: the addresses in a list that rejected mail lately (the Accounts page's bounce list, sent by the
// hub as `bounced`). Pure. [{ address, status, reason }]; [] when nothing matches or tracking is not live.
export function bouncedInList(recipients, bounced) {
  var bad = {};
  (Array.isArray(bounced) ? bounced : []).forEach(function (b) { if (b && b.address) bad[String(b.address).trim().toLowerCase()] = b; });
  var out = [];
  (recipients || []).forEach(function (r) {
    var b = bad[String(r || '').trim().toLowerCase()];
    if (b) out.push({ address: String(r).trim(), status: b.status || 'bounced', reason: b.reason || '' });
  });
  return out;
}
function bouncedHtml(list) {
  if (!list.length) return '';
  var esc = escapeHtml;
  return '<div style="margin-top:6px;padding:8px 10px;border-radius:8px;background:var(--danger-bg);border:1px solid var(--danger-deep);font-size:12px;color:var(--danger-deep)">' +
    '<b>This address rejected mail in the last 30 days, so mail to it may not arrive:</b>' +
    list.map(function (b) { return '<div><span translate="no">' + esc(b.address) + '</span>' + (b.reason ? ' (<span translate="no">' + esc(b.reason) + '</span>)' : '') + (b.status === 'complained' ? ' <span>marked as spam</span>' : '') + '</div>'; }).join('') +
    '<div style="margin-top:4px;color:var(--sub)">Check the address with the person, or with whoever runs that inbox.</div></div>';
}

var _hubData = null;

function hubApi(path, method, body) {
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, {
    method: method || 'GET', headers: headers, cache: 'no-store',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(function (res) {
    return res.json().catch(function () { return {}; }).then(function (d) {
      if (!res.ok || d.ok === false) throw new Error((d && (d.error || d.reason)) || ('HTTP ' + res.status));
      return d;
    });
  });
}

function lastLine(last) {
  if (!last) return 'No attempts recorded yet.';
  var when = String(last.at || last.ts || '').slice(0, 16).replace('T', ' ');
  var bits = ['Last: ' + (when || '?'), last.outcome || '?'];
  if (last.month) bits.push('month ' + last.month);
  if (last.via) bits.push('via ' + last.via);
  if (last.detail) bits.push(String(last.detail).slice(0, 120));
  return bits.join(' · ');
}

function cardHtml(cat) {
  var d = (_hubData && _hubData.emails && _hubData.emails[cat.id]) || { enabled: false, audience: 'list', recipients: [], last: null };
  var esc = escapeHtml;
  var audienceHtml;
  if (d.audience === 'list') {
    audienceHtml =
      '<div style="font-size:12px;color:var(--sub);margin-bottom:4px"><span>' + esc(cat.audienceKind || 'Audience') + '</span> — one email address per line:</div>' +
      '<textarea id="hubAud_' + cat.id + '" rows="3" class="form-input" style="font-size:12.5px;font-family:inherit" placeholder="name@algt-me.com">' + esc((d.recipients || []).join('\n')) + '</textarea>' +
      (cat.listNote ? '<div style="font-size:11.5px;color:var(--sub);margin-top:4px">' + esc(cat.listNote) + '</div>' : '') +
      warningsHtml(listWarnings(d.recipients, _hubData && _hubData.accountEmails)) +   // v133.72
      bouncedHtml(bouncedInList(d.recipients, _hubData && _hubData.bounced)) +   // 19 Oct 2026
      '<button class="btn btn-sm btn-outline" style="margin-top:6px" onclick="EmailHub.saveAudience(\'' + cat.id + '\')">💾 Save audience</button>';
  } else {
    audienceHtml = '<div style="font-size:12px;color:var(--sub)">' + esc(cat.audienceNote || '') + '</div>';
  }
  var whenText = cat.when;
  var scheduleHtml = '';
  if (d.schedule) {   // v133.56: a movable email; null means it keeps a fixed day
    whenText = describeSchedule(d.schedule) + ' morning (Dubai)';
    var freqLabel = { weekly: 'Weekly', fortnightly: 'Every other week', monthly: 'Monthly (first of that day)' };
    var note = coversNote(cat.id, d.schedule);
    scheduleHtml = '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:10px">' +
      '<span style="font-size:12px;color:var(--sub)">Sends</span>' +
      '<select id="hubFreq_' + cat.id + '" class="form-select" style="width:auto;font-size:12.5px">' + FREQS.map(function (f) { return '<option value="' + f + '"' + (f === d.schedule.freq ? ' selected' : '') + '>' + freqLabel[f] + '</option>'; }).join('') + '</select>' +
      '<span style="font-size:12px;color:var(--sub)">on</span>' +
      '<select id="hubDow_' + cat.id + '" class="form-select" style="width:auto;font-size:12.5px">' + DAY_NAMES.map(function (n, i) { return '<option value="' + i + '"' + (i === d.schedule.dow ? ' selected' : '') + '>' + n + '</option>'; }).join('') + '</select>' +
      '<button class="btn btn-sm btn-outline" onclick="EmailHub.saveSchedule(\'' + cat.id + '\')">Save day</button>' +
      '</div>' + (note ? '<div style="font-size:11.5px;color:var(--sub);margin-top:4px">' + esc(note) + '</div>' : '');
  }
  var testBtn = cat.test
    ? '<button class="btn btn-sm btn-outline" onclick="EmailHub.sendTest(\'' + cat.id + '\')">' + (cat.test.label || '📧 Send a test to me') + '</button>'
    : '';
  var sendBtn = cat.send
    ? '<button class="btn btn-sm btn-outline" onclick="EmailHub.sendNow(\'' + cat.id + '\')">📤 Send now</button>'
    : '';
  return '<div class="card" style="margin-bottom:14px">' +
    '<div style="display:flex;justify-content:space-between;gap:10px;align-items:flex-start;flex-wrap:wrap">' +
      '<div><div style="font-weight:700;font-size:14px">' + esc(cat.name) + '</div>' +
      '<div style="font-size:12px;color:var(--sub);margin-top:2px">' + esc(whenText) + '</div></div>' +
      (cat.alwaysOn   // 19 Oct 2026: an email with no switch of its own (leave_hr)
        ? '<span style="font-size:12.5px;flex:none;color:var(--ap-teal-text);font-weight:700">Always on</span>'
        : '<label style="display:flex;align-items:center;gap:6px;font-size:12.5px;cursor:pointer;flex:none">' +
          '<input type="checkbox" ' + (d.enabled ? 'checked ' : '') + 'onchange="EmailHub.toggle(\'' + cat.id + '\', this.checked)">' +
          (d.enabled ? '<b style="color:var(--ap-teal-text)">ON</b>' : '<b style="color:var(--sub)">OFF</b>') +
        '</label>') +
    '</div>' +
    scheduleHtml +
    '<div style="margin-top:10px">' + audienceHtml + (cat.id === 'jack_digest' ? ownersHtml() : '') + '</div>' +   // v132.73
    '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:10px;padding-top:10px;border-top:1px solid var(--border)">' +
      testBtn + sendBtn +
      '<span style="font-size:11.5px;color:var(--sub)">' + esc(lastLine(d.last)) + '</span>' +
    '</div>' +
  '</div>';
}

export async function renderEmailHub() {
  var root = document.getElementById('emailHubRoot');
  if (!root) return;
  root.innerHTML = '<div style="padding:24px;text-align:center;color:var(--sub);font-size:13px">Loading email settings…</div>';
  try {
    _hubData = await hubApi('/api/admin/email-hub');
  } catch (e) {
    root.innerHTML = '<div style="padding:24px;color:var(--danger-deep);font-size:13px">Could not load: ' + escapeHtml((e && e.message) || String(e)) + '</div>';
    return;
  }
  root.innerHTML =
    '<div style="font-size:12.5px;color:var(--sub);margin-bottom:14px">Every automated email Shift sends, in one place. The toggle controls the scheduled send; "Send now" goes to the real audience immediately.</div>' +
    summaryHtml() +   // v133.71
    HUB_CATALOG.map(cardHtml).join('');
  fillDigestOwners();   // v132.73: async, best-effort
}

export async function toggle(id, on) {
  try {
    await hubApi('/api/admin/email-hub/config', 'POST', { email: id, enabled: !!on });
    showToast((on ? 'ON — ' : 'OFF — ') + id.replace(/_/g, ' '), 'success');
    renderEmailHub();
  } catch (e) { showToast('Could not save: ' + e.message, 'error'); renderEmailHub(); }
}

// v132.73 (Matt: the digest's "needs a person" section "says Matt Kiernan - it
// should probably be the GM for people things, and the ops manager for operations
// ..."): the owners by domain, one per line as "domain: name". The domains
// are fixed in the Worker (OWNER_DOMAINS); the names are data, saved here.
function ownersHtml() {
  return '<div style="margin-top:10px;padding-top:10px;border-top:1px dashed var(--border)">' +
    '<div style="font-size:12px;color:var(--sub);margin-bottom:4px">Who owns the "needs a person" questions, one per line as <b translate="no">domain: name</b> ' +
    '(domains: people, operations, buying, replenishment, vm_marketing, delivery_apps):</div>' +
    '<textarea id="hubOwners_jack_digest" rows="6" class="form-input" style="font-size:12.5px;font-family:inherit" placeholder="people: (the General Manager)\noperations: ...">loading\u2026</textarea>' +
    '<button class="btn btn-sm btn-outline" style="margin-top:6px" onclick="EmailHub.saveDigestOwners()">\uD83D\uDCBE Save owners</button></div>';
}
async function fillDigestOwners() {
  var ta = document.getElementById('hubOwners_jack_digest');
  if (!ta) return;
  try {
    var d = await hubApi('/api/admin/jack-digest');
    var owners = (d && d.owners) || {};
    var domains = (d && d.owner_domains) || [];
    ta.value = domains.map(function (dm) { return dm.key + ': ' + (owners[dm.key] || ''); }).join('\n');
  } catch (e) { ta.value = ''; }
}
export function parseOwnersText(text) {
  var out = {};
  String(text || '').split(/\r?\n/).forEach(function (line) {
    var m = line.match(/^\s*([a-z_]+)\s*:\s*(.*?)\s*$/i);
    if (m && m[2]) out[m[1].toLowerCase()] = m[2];
  });
  return out;
}
export async function saveDigestOwners() {
  var ta = document.getElementById('hubOwners_jack_digest');
  if (!ta) return;
  try {
    var d = await hubApi('/api/admin/jack-digest/owners', 'POST', { owners: parseOwnersText(ta.value) });
    showToast('Owners saved: ' + Object.keys(d.owners || {}).length + ' set', 'success');
    fillDigestOwners();
  } catch (e) { showToast('Could not save: ' + e.message, 'error'); }
}
export async function saveAudience(id) {
  var ta = document.getElementById('hubAud_' + id);
  if (!ta) return;
  try {
    var d = await hubApi('/api/admin/email-hub/config', 'POST', { email: id, recipients: ta.value });
    var warn = listWarnings(d.recipients, _hubData && _hubData.accountEmails);   // v133.72
    if (warn.length) showToast('Saved, but ' + warn.length + ' address' + (warn.length === 1 ? ' is' : 'es are') + ' not a Shift account: check the spelling below');
    else showToast('Audience saved — ' + ((d.recipients || []).length) + ' address(es) kept', 'success');
    renderEmailHub();
  } catch (e) { showToast('Could not save: ' + e.message, 'error'); }
}

// v133.56: save one email's day and frequency
export async function saveSchedule(id) {
  var f = document.getElementById('hubFreq_' + id);
  var w = document.getElementById('hubDow_' + id);
  if (!f || !w) return;
  try {
    var d = await hubApi('/api/admin/email-hub/config', 'POST', { email: id, schedule: { freq: f.value, dow: Number(w.value) } });
    showToast('Saved: ' + describeSchedule(d.schedule), 'success');
    renderEmailHub();
  } catch (e) { showToast('Could not save: ' + e.message, 'error'); }
}

export async function sendTest(id) {
  var cat = HUB_CATALOG.filter(function (c) { return c.id === id; })[0];
  if (!cat || !cat.test) return;
  var to;
  if (cat.test.ask) {
    to = prompt('Send the test to which email address?');
    if (!to) return;
  }
  try {
    await hubApi(cat.test.path, cat.test.method, cat.test.body(to));
    showToast('Test sent ✔', 'success');
    renderEmailHub();
  } catch (e) { showToast('Test failed: ' + e.message, 'error'); }
}

export async function sendNow(id) {
  var cat = HUB_CATALOG.filter(function (c) { return c.id === id; })[0];
  if (!cat || !cat.send) return;
  if (!confirm('Send this email to its REAL audience now?')) return;
  try {
    await hubApi(cat.send.path, cat.send.method, cat.send.body());
    showToast('Sent ✔', 'success');
    renderEmailHub();
  } catch (e) { showToast('Send failed: ' + e.message, 'error'); }
}

window.renderEmailHub = renderEmailHub;
